import { DomainSchema } from '@multitec/domain'
import { useQuery } from 'react-query'
import { z } from 'zod'

import { Base } from '../base'
import { MyURLSearchParams } from '../utils/url-search-params'

class Create extends Base {
  private body = z.object({
    personId: z.string(),
    name: z.string(),
    location: z.string().optional(),
    startAt: z.coerce.date(),
    finishAt: z.coerce.date(),
    description: z.string().optional(),
  })

  private response = z.object({
    id: z.number(),
  })

  handle = async (body: z.infer<typeof this.body>) => {
    const { data } = await this.axios.post('/entity/contracts/', body)
    return this.response.parse(data.result)
  }
}

class Edit extends Base {
  private body = z.object({
    name: z.string(),
    location: z.string().optional(),
    password: z.string().optional(),
    active: z.boolean().optional(),
  })

  private response = z.object({
    id: z.number(),
  })

  handle = async (id: string | number, body: z.infer<typeof this.body>) => {
    const { data } = await this.axios.put('/entity/contracts/' + id, body)
    return this.response.parse(data.result)
  }
}

class Search extends Base {
  private searchParams = z.object({
    search: z.string().optional(),
    active: z.boolean().optional(),
  })

  private response = z.array(DomainSchema.Contract)

  handle = (searchParams: z.infer<typeof this.searchParams>) => {
    return useQuery(['contract', 'search', { ...searchParams }], async () => {
      const queryParams = new MyURLSearchParams(searchParams).toString()
      const { data } = await this.axios.get('/entity/contracts?' + queryParams)
      return this.response.parse(data.result)
    })
  }
}

class LoadByPerson extends Base {
  private response = z.array(DomainSchema.Contract)

  handle = (personId: string) => {
    return useQuery(['contract', 'load-by-person', { personId }], async () => {
      const queryParams = new MyURLSearchParams({ personId }).toString()
      const { data } = await this.axios.get('/entity/contracts?' + queryParams)
      return this.response.parse(data.result)
    })
  }
}

class LoadOne extends Base {
  private response = DomainSchema.Contract

  handle = (id: string | number) => {
    return useQuery(['contract', 'load-one', { id }], async () => {
      const { data } = await this.axios.get('/entity/contracts/' + id)
      return this.response.parse(data.result)
    })
  }
}

export default class extends Base {
  Create = new Create(this.axios).handle
  Edit = new Edit(this.axios).handle
  Search = new Search(this.axios).handle
  LoadByPerson = new LoadByPerson(this.axios).handle
  LoadOne = new LoadOne(this.axios).handle
}
